// ── ONE SPELLING FOR THREE ALPHABETS ─────────────────────────────────────────
// Tunify runs in Armenian, Russian and English, and its catalogue is written in
// whichever of those the shop happened to use. So a reader looking for a chair
// may type `աթոռ`, `ator`, `стул` or `chair`, and a listing titled `աթոռ` should
// answer the first two of those.
//
// THE TRICK IS NOT TO TRANSLATE. Translation needs a dictionary per word pair
// and gets `chair` → `աթոռ` wrong the moment somebody sells a `բազկաթոռ`.
// Transliteration needs only an alphabet, and it is what people actually do
// when they type Armenian on a Latin keyboard — `kahuyq` for `կահույք` is not a
// translation of anything, it is the same word spelled in the letters to hand.
//
// SO BOTH SIDES ARE REDUCED TO THE SAME LATIN SPELLING and compared there. That
// one decision handles every direction at once — Latin query against Armenian
// data, Armenian query against Russian data, and so on — where translating in
// one direction would have needed six.
//
// WHAT IT CANNOT DO, said plainly: this matches SOUNDS, not meanings. `стол`
// and `սեղան` are both "table" and neither will ever find the other here. That
// is a dictionary's job, and a dictionary is a much bigger thing to keep
// correct than an alphabet.

// ── ARMENIAN ─────────────────────────────────────────────────────────────────
// The Hübschmann-Meillet-ish romanisation people actually type, not the
// scholarly one: `ղ` is `gh` because that is what appears in a search box, not
// `ł`.
//
// ORDER MATTERS BELOW. `ու` is one sound (u) written with two letters, so it
// has to be consumed before `ո` and `ւ` are looked at individually — otherwise
// `կահույք` comes out `kahovyq` and matches nothing anyone would type.
const ARMENIAN_DIGRAPHS = [
  ['ու', 'u'],
  ['և', 'ev'],
];

const ARMENIAN = {
  ա: 'a', բ: 'b', գ: 'g', դ: 'd', ե: 'e', զ: 'z', է: 'e', ը: 'y',
  թ: 't', ժ: 'zh', ի: 'i', լ: 'l', խ: 'kh', ծ: 'ts', կ: 'k', հ: 'h',
  ձ: 'dz', ղ: 'gh', ճ: 'ch', մ: 'm', յ: 'y', ն: 'n', շ: 'sh', ո: 'o',
  չ: 'ch', պ: 'p', ջ: 'j', ռ: 'r', ս: 's', վ: 'v', տ: 't', ր: 'r',
  ց: 'ts', ւ: 'v', փ: 'p', ք: 'q', օ: 'o', ֆ: 'f',
};

// ── CYRILLIC ─────────────────────────────────────────────────────────────────
const CYRILLIC = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh',
  з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts',
  ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu',
  я: 'ya',
};

// ── WORD-INITIAL SOUNDS ──────────────────────────────────────────────────────
// Armenian `ե` is "ye" at the start of a word and "e" inside it; `ո` is "vo"
// and "o" the same way. Without this, `Երևան` reduces to `erevan` while anyone
// typing it in Latin writes `Yerevan` — and the city never matches itself.
const ARMENIAN_INITIAL = { ե: 'ye', ո: 'vo' };

/**
 * One word, transliterated. Split out because the initial-letter rules need to
 * know where a word begins, and the caller splits on whitespace to tell them.
 */
function translitWord(word) {
  let out = '';
  let i = 0;
  let atStart = true;

  while (i < word.length) {
    // Digraphs first — see the note on ARMENIAN_DIGRAPHS.
    const digraph = ARMENIAN_DIGRAPHS.find(([from]) => word.startsWith(from, i));
    if (digraph) {
      out += digraph[1];
      i += digraph[0].length;
      atStart = false;
      continue;
    }

    const ch = word[i];
    if (atStart && ARMENIAN_INITIAL[ch]) out += ARMENIAN_INITIAL[ch];
    else if (ARMENIAN[ch] !== undefined) out += ARMENIAN[ch];
    else if (CYRILLIC[ch] !== undefined) out += CYRILLIC[ch];
    else out += ch;                       // already Latin, a digit, punctuation

    // A letter that transliterated to nothing (ъ, ь) must not make the NEXT
    // letter look word-initial.
    atStart = false;
    i += 1;
  }
  return out;
}

// ── FORGIVING THE SPELLINGS NOBODY AGREES ON ─────────────────────────────────
// Two letters that sound alike are typed interchangeably, and a search box is
// the wrong place to be strict about it: `ք` is `q` in one romanisation and `k`
// in another, and the reader has no idea which one this app chose.
//
// APPLIED TO BOTH SIDES, which is what makes it safe — the query and the
// listing are folded the same way, so this can only widen what matches, never
// make the two disagree.
//
// Deliberately short. Every pair here costs a little precision, so it holds the
// confusions that actually happen (`ք`/`կ`, `ղ`/`գ`, `խ`/`հ`, `ձ`/`զ`,
// `ը`/`յ`/`ի`) and nothing speculative.
function fold(s) {
  return s
    // ── THE CHAT ALPHABET ──
    // Armenians typing Latin have their own conventions, and `x` for `խ` is the
    // one that turns up constantly — `xach` for `խաչ`. Rewritten to `kh` so it
    // meets the transliteration table's spelling, then folded with it.
    //
    // `x` is also used for `ղ` by some people, and that one is NOT handled: a
    // single canonical spelling cannot send one letter to both `h` and `g`.
    // `gh` and the plain Armenian both work for those.
    .replace(/x/g, 'kh')
    .replace(/gh/g, 'g')      // ղ heard as գ
    .replace(/kh/g, 'h')      // խ heard as հ
    .replace(/dz/g, 'z')      // ձ heard as զ
    // `ց` and `ծ` are `ts` in one romanisation and `c` in another, and Russian
    // `ц` is written both ways just as often. Levelling to `c` lets
    // `geghecik`, `gegetsik` and `գեղեցիկ` all be the same word.
    .replace(/ts/g, 'c')
    .replace(/q/g, 'k')       // ք heard as կ
    .replace(/w/g, 'v')
    .replace(/y/g, 'i')       // ը, յ and ի all reach for the same vowel
    // `աթոռ` has one ռ and `ատոռ` two t-sounds; doubling is not meaningful in
    // any of the three alphabets here, so it is levelled.
    .replace(/(.)\1+/g, '$1');
}

/**
 * The string a search compares on.
 *
 * Lowercased, accents stripped, transliterated to Latin, then folded. Run over
 * the query AND over everything being searched, so both meet in the same
 * alphabet.
 */
export function searchKey(s) {
  const lowered = String(s || '')
    .toLowerCase()
    // Splits `é` into `e` + a combining accent, which the next line removes —
    // so `José` typed as `Jose` still matches, as it always did.
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

  if (!lowered) return '';
  return fold(lowered.split(/\s+/).map(translitWord).join(' '));
}

// The old name, kept because three services call it that and the behaviour is a
// superset of what it used to do.
export default searchKey;
